import React, { useRef, useState } from 'react';
import { PropTypes } from 'prop-types';
import styled, { css } from 'styled-components';

import { Text, FontWeight } from '../Text';
import Icon from '../Icon';
import Colors from 'constants/Colors';
import Label from './Label';
import ValidationError from './ValidationError';
import { isEqualObject } from 'utils/src/object-util';
import { Events } from 'constants/Events';
import { Box } from 'components/Grid';
import Chip from 'components/Chip';

const propTypes = {
  placeholder: PropTypes.string,
  format: PropTypes.func,
  label: PropTypes.string,
  options: PropTypes.arrayOf(PropTypes.any).isRequired,
  tabIndex: PropTypes.number,
  isRequired: PropTypes.bool,
  optionMapper: PropTypes.func,
  onChange: PropTypes.func,
  onOpen: PropTypes.func,
  onClose: PropTypes.func
};

const defaultProps = {
  placeholder: '',
  format: selectedSize => `${selectedSize}`,
  label: null,
  isRequired: false,
  tabIndex: 0,
  optionMapper: opt => opt,
  onChange: value => value,
  onOpen: value => value,
  onClose: value => value
};

const SelectContainer = styled.div`
  position: relative;
  height: 42px;
  user-select: none;
  outline: none;
`;

const SelectedOptionContainer = styled.div`
  height: inherit;
  border: 1px solid ${Colors.GRAY[3]};
  background-color: ${Colors.WHITE};
  border-radius: 8px;
  display: flex;
  align-items: center;
  cursor: pointer;

  &:focus {
    border-color: ${Colors.SECONDARY};
  }

  ${({ isOpen }) =>
    isOpen &&
    css`
      border-bottom-left-radius: 0;
      border-bottom-right-radius: 0;
      border-color: ${Colors.SECONDARY};
    `}

  ${({ error, isOpen, touched }) =>
    error &&
    !isOpen &&
    touched &&
    css`
      border: 3px solid ${Colors.DANGER};

      & ${FontIcon} {
        color: ${Colors.DANGER};
      }
    `}
`;

const PlaceholderContainer = styled.div`
  margin-left: 8px;
  font-size: 14px;
  line-height: normal;
  flex: 1;
  color: ${Colors.GRAY[3]};
  min-width: 0;

  && ${Text} {
    display: block;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  ${({ hasValue }) =>
    hasValue &&
    css`
      color: ${Colors.PRIMARY};
    `}
`;

const FontIcon = styled(({ isOpen, ...props }) => <Icon {...props} />)`
  width: 20px;
  height: 20px;
  margin: 10px 6px 10px 10px;
  display: flex;
  justify-content: center;
  align-items: center;
  color: ${Colors.GRAY[3]};
  transition: transform 0.3s ease;

  ${({ isOpen }) =>
    isOpen &&
    css`
      color: ${Colors.SECONDARY};
      transform: rotate(180deg);
    `}
`;

const OptionsContainer = styled.ul`
  position: absolute;
  z-index: 10;
  top: 41px;
  left: 0;
  width: 100%;
  overflow-y: auto;
  overflow-x: hidden;
  max-height: 300px;
  margin: 0;
  padding: 0;
  box-sizing: border-box;
  border-left: 1px solid ${Colors.SECONDARY};
  border-right: 1px solid ${Colors.SECONDARY};
  border-bottom: 1px solid ${Colors.SECONDARY};
  background-color: #fff;
  border-bottom-left-radius: 4px;
  border-bottom-right-radius: 4px;
`;

const Option = styled.li`
  list-style: none;
  min-height: 42px;
  display: flex;
  align-items: center;
  padding: 10px;
  font-size: 14px;
  color: ${Colors.GRAY[0]};
  cursor: pointer;
  user-select: none;
  border-bottom: 1px solid ${Colors.GRAY[4]};

  ${({ selected }) =>
    selected &&
    css`
      background-color: ${Colors.Alpha(Colors.SECONDARY, 0.7)};
    `}

  &:hover {
    background-color: ${Colors.GRAY[4]};
  }
`;

const CheckBoxIcon = styled(Icon)`
  font-size: 14px;
  margin-right: 8px;

  ${({ checked }) =>
    checked &&
    `
    color: ${Colors.GRAY[0]};
  `}
`;

const MultiSelectField = ({
  placeholder,
  format,
  label,
  options,
  tabIndex,
  isRequired,
  optionMapper,
  isEqual,
  validationMinHeight,
  field,
  form: { setFieldValue, setFieldTouched, touched, errors },
  onChange,
  onClose,
  onOpen,
  chips,
  maxLength,
  ...props
}) => {
  const isEqualObjectFunc = isEqual ? isEqual : isEqualObject;
  const isClickOrTouchEvent = useRef(false);
  const [isOpen, setIsOpen] = useState(false);

  const meta = {
    isOpen,
    touched: touched[field.name],
    error: errors[field.name]
  };

  const _changeOpen = openState => {
    setIsOpen(openState);
    if (openState) {
      onOpen();
    } else {
      onClose();
    }
  };

  const _toggleField = () => {
    _changeOpen(!meta.isOpen);
  };

  // TODO: deixar uma unica função para controlar os clicks
  const _handleSelectedElements = (e, curr) => {
    e.stopPropagation();
    e.preventDefault();

    const mappedOption = optionMapper(curr);

    const selectedIndex = (field.value || []).findIndex(value =>
      isEqualObjectFunc(mappedOption, value)
    );
    const hasSelected = selectedIndex !== -1;
    const selectedValues = field.value ? [...field.value] : [];

    if (hasSelected) {
      selectedValues.splice(selectedIndex, 1);
    } else {
      selectedValues.push(mappedOption);
    }

    setFieldValue(field.name, selectedValues);
    onChange(selectedValues);
  };

  return (
    <div {...props}>
      {label && (
        <Label meta={meta} isRequired={isRequired} weight={FontWeight.Light}>
          {label}
        </Label>
      )}
      <SelectContainer
        data-name={field.name}
        onMouseDown={e => {
          isClickOrTouchEvent.current = e.button === Events.MOUSE_KEY_DOWN_CODE;
        }}
        onTouchStart={() => {
          isClickOrTouchEvent.current = true;
        }}
        onClick={_toggleField}
        tabIndex={tabIndex}
        onFocus={() => {
          _changeOpen(!isClickOrTouchEvent.current);
          isClickOrTouchEvent.current = false;
        }}
        onBlur={() => {
          setFieldTouched(field.name, true, true);
          _changeOpen(false);
          onClose();
        }}
        onKeyDown={e => {
          if (e.key.toLowerCase() === 'enter') {
            _toggleField();
          }
        }}
      >
        <SelectedOptionContainer {...meta}>
          <PlaceholderContainer hasValue={field.value}>
            {chips ? (
              <Box justifyContent="flex-start" style={{ overflowX: 'auto' }}>
                {field.value ? (
                  field.value.map((value, index) => (
                    <Chip
                      key={index}
                      label={value.label}
                      onClick={e => _handleSelectedElements(e, value)}
                    />
                  ))
                ) : (
                  <Text weight={FontWeight.SemiBold}>{placeholder}</Text>
                )}
              </Box>
            ) : (
              <Text weight={FontWeight.SemiBold}>
                {field.value ? format(field.value) : placeholder}
              </Text>
            )}
          </PlaceholderContainer>
          <FontIcon name="arrow-down" isOpen={meta.isOpen} />
        </SelectedOptionContainer>
        {meta.isOpen && (
          <OptionsContainer>
            {options.map((opt, index) => {
              const mappedOption = optionMapper(opt);
              const selectedIndex = (field.value || []).findIndex(value =>
                isEqualObjectFunc(mappedOption, value)
              );
              const hasSelected = selectedIndex !== -1;

              return (
                <Option
                  key={`opt-${field.name}-${index}`}
                  onClick={e => {
                    e.stopPropagation();
                    e.preventDefault();

                    const selectedValues = field.value ? [...field.value] : [];

                    if (hasSelected) {
                      selectedValues.splice(selectedIndex, 1);
                    } else {
                      if (!!maxLength && selectedValues.length === maxLength)
                        return;
                      selectedValues.push(mappedOption);
                    }

                    setFieldValue(field.name, selectedValues);
                    onChange(selectedValues);
                  }}
                  selected={hasSelected}
                >
                  {hasSelected ? (
                    <CheckBoxIcon name="check-checked" checked />
                  ) : (
                    <CheckBoxIcon name="check" />
                  )}
                  <Text weight={hasSelected ? FontWeight.Bold : null}>
                    {mappedOption.label}
                  </Text>
                </Option>
              );
            })}
          </OptionsContainer>
        )}
      </SelectContainer>
      <ValidationError meta={meta} validationMinHeight={validationMinHeight} />
    </div>
  );
};

MultiSelectField.propTypes = propTypes;
MultiSelectField.defaultProps = defaultProps;

export default MultiSelectField;
